import {
  Button,
  FormControl,
  FormLabel,
  Grid,
  OutlinedInput,
  Stack,
} from "@mui/material";
import { useCallback, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { gql, useMutation, useQuery } from "@apollo/client";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  ConditionalRenderer,
  useLanguageTranslator,
} from "../../../../../components";
import { useAuthSession } from "../../../../../components/AuthSessionProvider";
import { useNotify } from "../../../../../components/@common/NotificationsProvider";
import { ToastContainer } from "react-toastify";
import { toastHandler } from "../../../../../utils/helpers";
import { toastTypesKeys } from "../../../../../utils/constants";

// Define the GraphQL mutation
const CREATE_COUNTRY = gql`
  mutation CreateCountry($name: String!) {
    countryCreate(input: { countryInput: { name: $name } }) {
      clientMutationId
      country {
        id
        name
        createdAt
        updatedAt
      }
    }
  }
`;

const UPDATE_COUNTRY = gql`
  mutation UpdateCountry($id: ID!, $name: String!) {
    countryUpdate(input: { id: $id, countryInput: { name: $name } }) {
      clientMutationId
      country {
        id
        name
        createdAt
        updatedAt
      }
    }
  }
`;

const GET_COUNTRY = gql`
  query GetCountry($id: ID!) {
    country(id: $id) {
      id
      name
      createdAt
      updatedAt
    }
  }
`;

function CountryAction({ isUpdate = false }) {
  const navigate = useNavigate();
  const notify = useNotify();
  const { user } = useAuthSession() as any;
  const { translator, currentLanguage } = useLanguageTranslator();

  const [searchParams] = useSearchParams();
  const country_id = searchParams.get("id");

  const { data: countryData } = useQuery(GET_COUNTRY, {
    variables: { id: country_id },
    skip: !country_id,
    context: {
      headers: {
        "accept-language": currentLanguage || "en",
        "access-token": user?.accessToken,
        client: user?.client,
        uid: user?.email,
      },
    },
  });

  const [createCountry, { data, loading, error }] = useMutation(
    CREATE_COUNTRY,
    {
      context: {
        headers: {
          "accept-language": currentLanguage || "en",
          "access-token": user?.accessToken,
          client: user?.client,
          uid: user?.email,
        },
      },
    }
  );

  const [updateCountry] = useMutation(UPDATE_COUNTRY, {
    context: {
      headers: {
        "accept-language": currentLanguage || "en",
        "access-token": user?.accessToken,
        client: user?.client,
        uid: user?.email,
      },
    },
  });

  const initialValues: any = useMemo(() => {
    if (!countryData?.country) return undefined;

    const { name } = countryData?.country;

    return {
      country_name: name,
    };
  }, [countryData]);

  const form = useForm<any>({
    defaultValues: {
      country_name: "",
    },
    values: initialValues,
  });

  const handleFinish = useCallback(async (data: any, actionType: any) => {
    if (actionType === "update") {
      try {
        const response = await updateCountry({
          variables: {
            id: Number(country_id),
            name: data?.country_name,
          },
        });
        if (response.data) {
          notify.success(`Country Updated successfuly!`);
          setTimeout(() => {
            navigate("/admin/country");
            return;
          }, 1000);
        }
      } catch (err) {}
      return;
    }

    try {
      const response = await createCountry({
        variables: {
          name: data?.country_name,
        },
      });
      if (actionType === "save") {
        if (response.data) {
          notify.success(`Country Created successfuly!`);
          setTimeout(() => {
            navigate("/admin/country");
            return;
          }, 1000);
        }
      } else if (actionType === "saveAndAddNew") {
        if (response.data) {
          notify.success(`Country Created successfuly!`);
          setTimeout(() => {
            form?.reset();
            return;
          }, 1000);
        }
      }
    } catch (err) {
      const errorCatch = JSON.stringify(err);
      const parsedError = JSON.parse(errorCatch);

      toastHandler(parsedError?.message || "", toastTypesKeys.error);
    }
  }, []);

  return (
    <>
      <ToastContainer />
      <form onSubmit={form.handleSubmit((data) => handleFinish(data, "save"))}>
        <Grid
          container
          px={4}
          sx={{ backgroundColor: "#ffffff", borderRadius: "10px" }}
          spacing={2}
        >
          <Grid item xs={12} sm={6} lg={6}>
            <FormControl required disabled={false} fullWidth margin="normal">
              <FormLabel>
                {translator("Country")} {translator("dashboardTableHead.Name")}
              </FormLabel>
              <OutlinedInput
                {...form?.register("country_name", { required: "Required!" })}
              />
              {/* <FormHelperText>{errors.title?.message}</FormHelperText> */}
            </FormControl>
          </Grid>
        </Grid>
        <Stack
          sx={{ backgroundColor: "#ffffff" }}
          direction={"row"}
          justifyContent={"flex-end"}
          gap={2}
          px={4}
          py={4}
        >
          <Link
            to="/admin/country"
            onClick={() => form?.reset()}
            style={{
              backgroundColor: "#e0e0e0",
              padding: "8px 12px",
              color: "#989C9F",
              borderRadius: "10px",
              fontSize: "14px",
              textDecoration: "none",
            }}
          >
            {translator("Cancel")}
          </Link>
          <Button
            type="button"
            onClick={form.handleSubmit((data) =>
              handleFinish(data, isUpdate ? "update" : "save")
            )}
            style={{
              backgroundColor: "#00AAB0",
              padding: "8px 12px",
              color: "white",
              borderRadius: "10px",
              fontSize: "14px",
              textDecoration: "none",
            }}
          >
            {isUpdate
              ? translator("generic.Update")
              : translator("generic.Save")}
          </Button>
          <ConditionalRenderer condition={!isUpdate}>
            <Button
              type="button"
              onClick={form.handleSubmit((data) =>
                handleFinish(data, "saveAndAddNew")
              )}
              style={{
                backgroundColor: "#F6A000",
                padding: "8px 12px",
                color: "white",
                borderRadius: "10px",
                fontSize: "14px",
                textDecoration: "none",
              }}
            >
              {translator("generic.Save")} & {translator("generic.Add New")}
            </Button>
          </ConditionalRenderer>
        </Stack>
      </form>
    </>
  );
}

export { CountryAction };
